import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button } from '@/components/Shared/Button';
import { CountryDropDown } from '@/components/Shared/CountryDropDown';
import { CustomModal } from '@/components/Shared/CustomModal';
import { StatesDropDown } from '@/components/Shared/StatesDropDown';
import { TextInput } from '@/components/Shared/TextInput';
import PricingCard from '@/components/Profile/PaymentModal/PricingCard';
import BillingModal from '@/components/Profile/PaymentModal/BillingModal';
import {
  statesAPIFn,
  countriesAPIFn
} from '../../../redux/features/getCountriesStates/getStatCountriesStatesSlice';
import {
  chargebeePaymentsThunk,
  getTaxAmountThunk
} from '../../../redux/features/payment/paymentSlice';
import { getUserPlanTypeAPIFn } from '@/redux/features/CurrentUser/currentUserSlice';
import { CustomerPopover } from '@/components/Shared/Popover/CustomerPopover';
import { ShortArrowIcon } from '@/assets/icons/ShortArrowIcon';
import { customToast, trackUserEvent, USER_EVENTS, useKitStates } from '@/utils';
import { coreDomain } from '@/redux/constant';
import { normalizeRole, ROLES } from '@/router/roles';

const EMPTY_ARRAY = [];

export default function PaymentModal({ isOpen, setIsOpen, onSuccessPayment, onBack }) {
  const dispatch = useDispatch();
  const { userId } = useKitStates();
  const ref = useRef(null);
  const scrollContainerRef = useRef(null);
  const billingDetailsRef = useRef(null);
  const hasGuidedToBillingRef = useRef(false);
  const hasTrackedCheckoutStartRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [showBillingHint, setShowBillingHint] = useState(false);
  const [braintreeInstance, setBraintreeInstance] = useState(null);
  const [prefetchedPaymentPayload, setPrefetchedPaymentPayload] = useState(null);
  const [braintreeResetToken, setBraintreeResetToken] = useState(0);
  const [openCountryPopover, setOpenCountryPopover] = useState(false);
  const [openCityPopover, setOpenCityPopover] = useState(false);
  const [paymentErr, setPaymentErr] = useState(null);
  const mediaKitAmount = 9

  const user = useSelector(state => state?.currentUser?.currentUser);

  const { taxAmount } = useSelector(state => state?.payment);
  const countries = useSelector(state => state?.countriesAndStates?.countries ?? EMPTY_ARRAY);
  const countriesLoading = useSelector(state => state?.countriesAndStates?.countriesLoading);
  const statesList = useSelector(state => state?.countriesAndStates?.states ?? EMPTY_ARRAY);
  const isRehydrated = useSelector(state => state?._persist?.rehydrated);
  const hasRequestedCountriesRef = useRef(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    country: null,
    countryName: '',
    state: null,
    stateName: '',
    zipCode: ''
  });

  const splitFullName = useCallback((fullName = '') => {
    const cleaned = String(fullName).trim().replace(/\s+/g, ' ');
    if (!cleaned) return { firstName: '', lastName: '' };
    const parts = cleaned.split(' ');
    return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' };
  }, []);

  const getNameFromBraintreePayload = useCallback((payload) => {
    const fullNameCandidates = [
      payload?.details?.cardholderName,
      payload?.details?.billingAddress?.name,
      payload?.details?.billingAddress?.recipientName,
      payload?.details?.shippingAddress?.name,
      payload?.details?.payerName,
      payload?.details?.username,
      payload?.details?.googlePayCardDetails?.cardholderName,
      payload?.details?.applePayCardDetails?.cardholderName,
      // Google Pay (rawPaymentData payload shape)
      payload?.details?.rawPaymentData?.paymentMethodData?.info?.billingAddress?.name,
      payload?.rawPaymentData?.paymentMethodData?.info?.billingAddress?.name,
      payload?.details?.billingContact?.name,
      payload?.details?.billingContact?.givenName && payload?.details?.billingContact?.familyName
        ? `${payload.details.billingContact.givenName} ${payload.details.billingContact.familyName}`
        : null
    ].filter(Boolean);

    if (fullNameCandidates.length) return splitFullName(fullNameCandidates[0]);

    const structuredFirst =
      payload?.details?.firstName ||
      payload?.details?.billingAddress?.firstName ||
      payload?.details?.billingContact?.givenName ||
      payload?.details?.payerFirstName ||
      '';

    const structuredLast =
      payload?.details?.lastName ||
      payload?.details?.billingAddress?.lastName ||
      payload?.details?.billingContact?.familyName ||
      payload?.details?.payerLastName ||
      '';

    if (structuredFirst || structuredLast) {
      return {
        firstName: String(structuredFirst).trim(),
        lastName: String(structuredLast).trim()
      };
    }

    return { firstName: '', lastName: '' };
  }, [splitFullName]);

  useEffect(() => {
    if (!prefetchedPaymentPayload) return;

    const extracted = getNameFromBraintreePayload(prefetchedPaymentPayload);
    if (!extracted?.firstName && !extracted?.lastName) return;

    setFormData(prev => ({
      ...prev,
      firstName: (prev?.firstName || '').trim() ? prev.firstName : extracted.firstName,
      lastName: (prev?.lastName || '').trim() ? prev.lastName : extracted.lastName
    }));
  }, [prefetchedPaymentPayload, getNameFromBraintreePayload]);

  useEffect(() => {
    if (!prefetchedPaymentPayload) return;
    if (!Array.isArray(countries) || countries.length === 0) return;

    const countryCode =
      prefetchedPaymentPayload?.details?.rawPaymentData?.paymentMethodData?.info?.billingAddress?.countryCode ||
      prefetchedPaymentPayload?.rawPaymentData?.paymentMethodData?.info?.billingAddress?.countryCode ||
      prefetchedPaymentPayload?.details?.countryCode ||
      '';

    if (!countryCode) return;

    const matched = countries.find(
      c => String(c?.countryCode || '').toUpperCase() === String(countryCode).toUpperCase()
    );

    if (!matched) return;

    setFormData(prev => {
      // Do not override if user already selected a country manually.
      if (prev?.country) return prev;
      // Load US states list when wallet selects United States (same as manual country click).
      if (String(matched?.countryCode || '').toUpperCase() === 'US') {
        dispatch(statesAPIFn({ country: matched?.name?.toLowerCase() }));
      }
      return {
        ...prev,
        country: matched,
        countryName: matched?.name || prev?.countryName || ''
      };
    });
  }, [prefetchedPaymentPayload, countries, dispatch]);

  // Google Pay (and similar): billing administrativeArea = US state code, postalCode = zip
  useEffect(() => {
    if (!prefetchedPaymentPayload) return;

    const billing =
      prefetchedPaymentPayload?.details?.rawPaymentData?.paymentMethodData?.info?.billingAddress ||
      prefetchedPaymentPayload?.rawPaymentData?.paymentMethodData?.info?.billingAddress ||
      null;

    const adminArea = billing?.administrativeArea?.trim?.() || '';
    const postal = billing?.postalCode?.trim?.() || '';
    if (!adminArea && !postal) return;

    setFormData(prev => {
      if (prev.countryName !== 'United States') return prev;

      let next = { ...prev };
      let changed = false;

      if (!prev.state?.stateCode && adminArea && Array.isArray(statesList) && statesList.length > 0) {
        const matchedState = statesList.find(
          s => String(s?.stateCode || '').toUpperCase() === adminArea.toUpperCase()
        );
        if (matchedState) {
          next = { ...next, state: matchedState, stateName: matchedState.name };
          changed = true;
        }
      }

      if (postal && !(prev.zipCode || '').trim()) {
        next = { ...next, zipCode: postal };
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [prefetchedPaymentPayload, statesList, formData.countryName]);

  const isFormValid = Boolean(
    formData?.firstName?.trim() &&
    formData?.lastName?.trim() &&
    formData?.email?.trim() &&
    formData?.country &&
    formData?.zipCode?.trim() &&
    hasPaymentMethod
  );

  const userRole = normalizeRole(user?.userRole);

  useEffect(() => {
    if (!isOpen) {
      hasGuidedToBillingRef.current = false;
      hasTrackedCheckoutStartRef.current = false;
      setShowBillingHint(false);
    }
  }, [isOpen]);

  const markCheckoutStarted = useCallback(() => {
    if (hasTrackedCheckoutStartRef.current) return;
    hasTrackedCheckoutStartRef.current = true;
    trackUserEvent(USER_EVENTS.CHECKOUT_FORM_STARTED, userId);
  }, [userId]);

  useEffect(() => {
    if (!hasPaymentMethod) {
      hasGuidedToBillingRef.current = false;
      setShowBillingHint(false);
      return;
    }

    setShowBillingHint(true);

    if (hasGuidedToBillingRef.current) return;
    hasGuidedToBillingRef.current = true;

    const scrollToBilling = () => {
      const container = scrollContainerRef.current;
      const billingSection = billingDetailsRef.current;

      if (container && billingSection) {
        const containerTop = container.getBoundingClientRect().top;
        const billingTop = billingSection.getBoundingClientRect().top;
        container.scrollTo({
          top: container.scrollTop + (billingTop - containerTop) - 8,
          behavior: 'smooth'
        });
        return;
      }

      billingSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToBilling);
    });
  }, [hasPaymentMethod]);

  const dismissBillingHint = useCallback(() => {
    setShowBillingHint(false);
  }, []);

  // Fetch countries when component mounts (creators only)
  useEffect(() => {
    if (userRole !== ROLES.CREATOR) return;
    if (!userId) return;
    if (!isRehydrated) return;
    if (hasRequestedCountriesRef.current) return;
    if (countriesLoading) return;
    if (Array.isArray(countries) && countries.length > 0) return;

    hasRequestedCountriesRef.current = true;
    dispatch(countriesAPIFn({ userId }));
  }, [dispatch, userId, isRehydrated, countries, countriesLoading, userRole]);

  const handleChange = async e => {
    markCheckoutStarted();
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'zipCode') {
      await calculateTax(formData.country?.countryCode, formData.state?.stateCode, value);
    }
  };

  const calculateTax = async (countryCode, stateCode, zipCode) => {
    if (!countryCode) return;

    try {
      setIsLoading(true);

      const taxParams = {
        country: countryCode,
        state: stateCode || 'strings',
        zipCode: zipCode || '',
        amount: mediaKitAmount
      };

      await dispatch(getTaxAmountThunk(taxParams));
    } catch (err) {
      console.error('Tax calculation error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    if (isLoading) return;

    // Validate all required fields
    const requiredFields = [
      { value: formData?.firstName, message: 'Please enter your First Name' },
      { value: formData?.lastName, message: 'Please enter your Last Name' },
      { value: formData?.email, message: 'Please enter your Email' },
      { value: formData?.country, message: 'Please select your Country' },
      { value: formData?.zipCode, message: 'Please enter your Zip Code' },
      { value: hasPaymentMethod, message: 'Please select a payment method' }
    ];

    for (const field of requiredFields) {
      if (!field.value) {
        customToast.error(field.message);
        return;
      }
    }

    setIsLoading(true);

    try {
      // Check if customer exists
      const existingCustomerResponse = await fetch(
        `${coreDomain}/ChargeBeePayments/CheckCustomer?email=${encodeURIComponent(formData?.email?.trim())}`
      );
      const existingCustomerData = await existingCustomerResponse.json();

      if (existingCustomerData?.exists) {
        customToast.error(
          'A customer with this email already exists. Please use a different email or log in.'
        );
        setIsLoading(false);
        return;
      }

      // Retrieve payment method
      if (!braintreeInstance) {
        throw new Error('Braintree instance is not available');
      }

      const payload =
        prefetchedPaymentPayload?.nonce
          ? prefetchedPaymentPayload
          : await new Promise((resolve, reject) => {
            braintreeInstance.requestPaymentMethod((err, res) => {
              if (err) {
                reject(err);
              } else {
                resolve(res);
              }
            });
          });

      if (!payload) {
        customToast.error('Unable to retrieve payment details. Please try again.');
        setIsLoading(false);
        return;
      }

      const extractedName = getNameFromBraintreePayload(payload);
      const finalFirstName = (formData?.firstName || '').trim() || extractedName.firstName;
      const finalLastName = (formData?.lastName || '').trim() || extractedName.lastName;

      if (extractedName.firstName || extractedName.lastName) {
        setFormData(prev => ({
          ...prev,
          firstName: (prev?.firstName || '').trim() ? prev.firstName : extractedName.firstName,
          lastName: (prev?.lastName || '').trim() ? prev.lastName : extractedName.lastName
        }));
      }

      if (!finalFirstName) {
        customToast.error('Please enter your First Name');
        setIsLoading(false);
        return;
      }

      if (!finalLastName) {
        customToast.error('Please enter your Last Name');
        setIsLoading(false);
        return;
      }

      // Process payment using Redux thunk
      const response = await dispatch(
        chargebeePaymentsThunk({
          nonce: payload?.nonce,
          customerEmail: formData?.email?.trim(),
          paymentType: payload?.type,
          firstName: finalFirstName,
          lastName: finalLastName,
          country: formData?.country?.countryCode === 'UK' ? 'GB' : formData?.country?.countryCode,
          zipCode: formData?.zipCode?.trim(),
          state: formData?.state?.stateCode,
          userId: userId
        })
      );

      if (response?.payload?.result?.success) {
        trackUserEvent(USER_EVENTS.PURCHASE_SUCCESS, userId);
        setIsOpen(false);
        await dispatch(getUserPlanTypeAPIFn({ userId: userId }));
        if (onSuccessPayment) {
          await onSuccessPayment();
        }
      } else {
        handlePaymentError(response);
      }
    } catch (error) {
      handlePaymentError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentError = error => {

    setPrefetchedPaymentPayload(null);
    setHasPaymentMethod(false);
    // The Braintree nonce is single-use — even on failure, the backend may
    // have already consumed it against Braintree. Re-initializing the drop-in
    // forces the user to reselect/reapprove payment so the retry gets a nonce
    // that was never sent anywhere, instead of the drop-in silently handing
    // back the same (now-burned) one for wallet methods like PayPal/Google Pay.
    setBraintreeResetToken(prev => prev + 1);

    const errorMessage =
      error?.payload?.responseException?.exceptionMessage ||
      error?.response?.data?.responseException?.exceptionMessage ||
      error.message ||
      'An unexpected error occurred. Please try again.';

    if (errorMessage === 'Processor Declined') {
      setPaymentErr(true);
    } else {
      customToast.error(errorMessage);
    }
  };

  return (
    <CustomModal
      title='Complete your payment'
      open={isOpen}
      leftAligned={false}
      onClose={() => setIsOpen(false)}
      isBack={!!onBack}
      onBack={onBack}
      maxWidth='md'
      height='max-content'
    >
      <div className='flex justify-center'>
        <div className='w-full sm:w-[450px] rounded-[4px] py-4 my-5 mx-4 border border-black flex justify-center items-center flex-col gap-5'>
          <div ref={scrollContainerRef} className='w-full max-h-[25rem] overflow-auto space-y-4 px-5'>
            {paymentErr && (
              <div className='text-red-500'>
                Insufficient balance! Add balance and try again, or select another payment method.
              </div>
            )}

            <div className='w-full sm:w-[402px] min-h-[263px] p-4 border-[0.5px] rounded-[4px] border-neutral-light1'>
              <BillingModal
                onPaymentMethodAvailable={() => {
                  markCheckoutStarted();
                  setHasPaymentMethod(true);
                }}
                onPaymentMethodNotAvailable={() => setHasPaymentMethod(false)}
                onBraintreeInstance={setBraintreeInstance}
                onPaymentPayload={setPrefetchedPaymentPayload}
                resetToken={braintreeResetToken}
              />
            </div>

            {hasPaymentMethod ? (
              <>
                {showBillingHint ? (
                  <button
                    type='button'
                    onClick={() => {
                      dismissBillingHint();
                      billingDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className='flex items-start w-full gap-3 px-3 py-3 mt-3 text-left transition-colors border border-black rounded bg-secondary-lightMintGreen/40 hover:bg-secondary-lightMintGreen/60'
                  >
                    <ShortArrowIcon className='mt-0.5 size-5 shrink-0 rotate-90' />
                    <span className='text-sm leading-snug text-black font-outfit'>
                      <span className='font-medium'>Payment method added.</span>{' '}
                      Scroll down to complete your name, email, and billing details.
                    </span>
                  </button>
                ) : null}

                <div
                  ref={billingDetailsRef}
                  onFocusCapture={dismissBillingHint}
                  className={`space-y-4 transition-shadow duration-500`}
                >
                  <div className='flex flex-col w-[100%] sm:flex-row gap-5 mt-3'>
                    <TextInput
                      type='text'
                      name='firstName'
                      label='First Name'
                      value={formData.firstName}
                      onChange={handleChange}
                      onFocus={dismissBillingHint}
                      required
                    />
                    <TextInput
                      type='text'
                      name='lastName'
                      label='Last Name'
                      value={formData.lastName}
                      onChange={handleChange}
                      onFocus={dismissBillingHint}
                      required
                    />
                  </div>

                  <TextInput
                    type='email'
                    name='email'
                    label='Email Address'
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={dismissBillingHint}
                    required
                  />

                  <CustomerPopover
                    zIndex={999999}
                    placement='bottom'
                    contentClassName={'!p-0 bg-white max-w-[35rem]'}
                    borderColor='#D9D9D9'
                    open={openCountryPopover}
                    onOpenChange={(nextOpen) => {
                      if (nextOpen) markCheckoutStarted();
                      setOpenCountryPopover(nextOpen);
                      if (nextOpen) setOpenCityPopover(false);
                    }}
                    className={'w-full'}
                    content={
                      <div className='w-[18rem] xs:w-[25rem]' ref={ref}>
                        <CountryDropDown
                          onCountryClick={async (country) => {
                            setFormData(prev => ({
                              ...prev,
                              country: country,
                              countryName: country?.name,
                              state: null,
                              stateName: '',
                            }))
                            if (country?.name?.toLowerCase()) {
                              await dispatch(statesAPIFn({
                                country: country?.name?.toLowerCase()
                              }));
                            }
                            setOpenCountryPopover(false);
                          }}
                        />
                      </div>
                    }
                  >
                    <div
                      className={`h-[50px] border border-black w-full flex items-center px-3 ${formData?.countryName === '' ? 'text-black/60' : ''}`}
                    >
                      <p className={`w-full pl-1.5 font-outfit`}>
                        {formData?.countryName || 'Select Country'}
                      </p>

                      <ShortArrowIcon className={`w-5 h-5 -translate-y-0.5 -rotate-90 transition-all duration-200 ${openCountryPopover ? 'rotate-90' : ''}`} />
                    </div>
                  </CustomerPopover>

                  {formData?.countryName === "United States" ? (
                    <CustomerPopover
                      zIndex={999999}
                      placement='bottom'
                      contentClassName={'!p-0 bg-white max-w-[35rem]'}
                      borderColor='#D9D9D9'
                      open={openCityPopover}
                      onOpenChange={(nextOpen) => {
                        if (nextOpen && !formData?.countryName) {
                          customToast.error('Please select your country');
                          return;
                        }
                        if (nextOpen) markCheckoutStarted();
                        setOpenCityPopover(nextOpen);
                        if (nextOpen) setOpenCountryPopover(false);
                      }}
                      className={'w-full'}
                      content={
                        <div className='w-[18rem] xs:w-[25rem]' ref={ref}>
                          <StatesDropDown
                            countryName={formData?.countryName}
                            onStateClick={async (state) => {
                              setFormData(prev => ({
                                ...prev,
                                state: state,
                                stateName: state?.name,
                                zipCode: '',
                              }))
                              setOpenCityPopover(false);
                            }}
                          />
                        </div>
                      }
                    >
                      <div
                        className={`h-[50px] border border-black w-full flex items-center px-3 ${formData?.stateName === '' ? 'text-black/60' : ''}`}
                      >
                        <p className={`pl-1.5 font-outfit w-full`}>
                          {formData?.stateName || 'Select State'}
                        </p>

                        <ShortArrowIcon className={`size-5 -translate-y-0.5 -rotate-90 ${openCityPopover ? 'rotate-90' : ''}`} />
                      </div>
                    </CustomerPopover>
                  ) : null}

                  {formData?.countryName ? (
                    <TextInput
                      onChange={handleChange}
                      value={formData.zipCode}
                      name='zipCode'
                      label='Zip Code'
                    />
                  ) : null}

                  <PricingCard mediaKitAmount={mediaKitAmount} taxAmount={taxAmount} />
                </div>
              </>
            ) : null}
          </div>

          <div className='w-full px-5'>
            <Button
              variant='contained'
              color='secondary.lightMintGreen'
              disabled={isLoading || !isFormValid}
              radius='4px'
              textColor='black'
              width='100%'
              height='60px'
              className='!uppercase !border-[1px] !px-3 !border-solid !border-black !text-sm !font-baskervville !sm:text-base'
              onClick={handlePayment}
            >
              {isLoading ? 'Processing...' : 'START FREE TRIAL'}
            </Button>

            <p className="text-sm text-neutral leading-[115%] mt-3">You’ll receive a 14-day free trial. You won’t be charged until the trial ends.</p>
          </div>

        </div>
      </div>
    </CustomModal>
  );
}
